'use client'

import { SkeletonCircle, SkeletonText } from '@sushiswap/ui'
import { useMemo } from 'react'
import { KATANA_CHAIN_ID } from 'src/config'
import { useClaimableRewards } from 'src/lib/hooks/react-query/rewards'
import { formatUSD } from 'sushi'
import { isMerklChainId } from 'sushi/evm'
import { useConnection } from 'wagmi'
import { PortfolioMerklClaimables } from './portfolio-merkl-claimables'

export const PortfolioClaimables = () => {
  const { address } = useConnection()

  const { data, isLoading, isError } = useClaimableRewards({
    chainIds: isMerklChainId(KATANA_CHAIN_ID) ? [KATANA_CHAIN_ID] : [],
    account: address,
    enabled: !!address,
  })

  const katanaRewards = data?.[KATANA_CHAIN_ID]

  const rewards = useMemo(() => {
    if (!katanaRewards) return []
    return Object.entries(katanaRewards.rewardAmounts).map(
      ([tokenAddress, amount]) => ({
        tokenAddress,
        amount,
        amountUSD: katanaRewards.rewardAmountsUSD[tokenAddress] ?? 0,
      }),
    )
  }, [katanaRewards])

  const totalUSD = katanaRewards?.totalRewardsUSD ?? 0

  return (
    <div className="flex flex-col h-full overflow-hidden gap-y-5">
      <div className="px-5">
        <div className="flex flex-col px-5 py-3 gap-y-3 bg-secondary rounded-xl">
          <span className="text-sm text-muted-foreground">Total Claimable</span>
          {isLoading ? (
            <SkeletonText fontSize="lg" className="!w-1/3" />
          ) : (
            <>
              <div className="text-2xl font-bold">{formatUSD(totalUSD)}</div>
              {isError ? (
                <div className="text-xs italic text-red-500">
                  An error occurred fetching claimable rewards.
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
      {isError ? null : isLoading ? (
        <div>
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={`${i}`}
              className="flex items-center w-full px-5 py-3 gap-x-7"
            >
              <SkeletonCircle radius={28} />
              <div className="flex items-center justify-between w-full gap-x-3">
                <div className="flex flex-col basis-3/4 gap-y-1">
                  <SkeletonText fontSize="sm" />
                  <SkeletonText fontSize="xs" />
                </div>
                <div className="flex flex-col basis-1/4 gap-y-1">
                  <SkeletonText fontSize="sm" />
                  <SkeletonText fontSize="xs" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : rewards.length ? (
        <PortfolioMerklClaimables rewards={rewards} />
      ) : (
        <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
          No claimable rewards
        </div>
      )}
    </div>
  )
}
